/** XeAgents.ai**/
import { SEOHead } from "@/components/landing/SEOHead";
import { Navbar } from "@/components/landing/Navbar";
import { LandingSection } from "@/components/landing/LandingSection";
import { VoiceProHero } from "@/components/landing/VoiceProHero";
import { CapabilityTiles } from "@/components/landing/CapabilityTiles";
import { PlatformCapabilities } from "@/components/landing/PlatformCapabilities";
import { FreeCreditsBanner } from "@/components/landing/FreeCreditsBanner";
import { OutcomesSection } from "@/components/landing/OutcomesSection";
import { SectorsStrip } from "@/components/landing/SectorsStrip";
import { AITechnologyShowcase } from "@/components/landing/AITechnologyShowcase";
import { HowItWorks } from "@/components/landing/HowItWorks";
import { UseCaseTabs } from "@/components/landing/UseCaseTabs";
import { IntegrationsGrid } from "@/components/landing/IntegrationsGrid";
import { PricingSection } from "@/components/landing/PricingSection";
import { ContactSection } from "@/components/landing/ContactSection";
import { Footer } from "@/components/landing/Footer";
import { VoiceCallWidget } from "@/components/landing/VoiceCallWidget";
import { AuthDialogProvider } from "@/components/landing/AuthDialog";
import { PilotRequestDialogProvider } from "@/components/landing/PilotRequestDialog";
import { useLandingSeo } from "@/hooks/useLandingSeo";
import { useSectionHashScroll } from "@/lib/section-nav";

export default function LandingPage() {
  const seo = useLandingSeo();

  // Makes "/#pricing" (and every other section link from off this page) land
  // on the section rather than at the top.
  useSectionHashScroll();

  return (
    <PilotRequestDialogProvider>
      <AuthDialogProvider>
      <div
        className="min-h-screen bg-background overflow-x-hidden"
        data-testid="landing-page"
      >
        <SEOHead {...seo} />

        <Navbar />

        <main role="main" itemScope itemType="https://schema.org/WebPage">
          {/*
            The hero carries the page h1 and the free-credits offer. The
            "hero-description" class is the selector the speakable schema points
            voice assistants at, so it moves here with the h1 - see
            useLandingSeo.
          */}
          <LandingSection
            label="AI voice agents for healthcare operations"
            className="hero-description"
            itemProp="mainContentOfPage"
          >
            <VoiceProHero />
          </LandingSection>

          {/* The four healthcare workflows sit directly under the hero. */}
          <LandingSection
            id="outcomes"
            label="Healthcare workflows"
            schemaType="ItemList"
          >
            <OutcomesSection />
          </LandingSection>

          <LandingSection id="sectors" label="Also built for" schemaType="ItemList">
            <SectorsStrip />
          </LandingSection>

          <LandingSection
            id="capabilities"
            label="What VoicePro does"
            className="key-features"
            schemaType="ItemList"
          >
            <CapabilityTiles />
          </LandingSection>

          <LandingSection
            id="platform"
            label="Everything inside the platform"
            className="key-features"
            schemaType="ItemList"
          >
            <PlatformCapabilities />
          </LandingSection>

          <LandingSection label="Free voice credits offer">
            <FreeCreditsBanner />
          </LandingSection>

          {/*
            FeaturesCarousel is not shown: it carries a 99.9% uptime SLA, a 24/7
            availability stat and a generic "enterprise-grade security" claim, none of
            which we can back for a healthcare buyer. Restore it once they are verified.
          */}

          <LandingSection
            id="ai-technology"
            label="AI Technology"
            className="key-features"
            schemaType="ItemList"
          >
            <AITechnologyShowcase />
          </LandingSection>

          <LandingSection
            id="how-it-works"
            label="How It Works"
            schemaType="HowTo"
          >
            <HowItWorks />
          </LandingSection>

          <LandingSection
            id="use-cases"
            label="Use Cases"
            schemaType="ItemList"
          >
            <UseCaseTabs />
          </LandingSection>

          <LandingSection
            id="integrations"
            label="Integrations"
            schemaType="ItemList"
          >
            <IntegrationsGrid />
          </LandingSection>

          <LandingSection
            id="pricing"
            label="Pricing"
            schemaType="ItemList"
          >
            <PricingSection />
          </LandingSection>

          <LandingSection
            id="contact"
            label="Contact"
            schemaType="ContactPage"
          >
            <ContactSection />
          </LandingSection>
        </main>

        {/* Floating / global UI elements */}
        <VoiceCallWidget />

        <Footer />
      </div>
      </AuthDialogProvider>
    </PilotRequestDialogProvider>
  );
}
